import React, { useState } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import Error403Page from "../Error_backend";

export interface TabComponentProps {
  data?: ServicesStatsData;
  loading?: boolean;
  error?: string | null;
  dateRange?: { startDate: string; endDate: string };
  onRetry?: () => void;
}

export interface ServicesStatsData {
  // A. Inventory de Servicios
  inventory: {
    totalActiveServices: number;
    totalServiceTypes: number;
    servicesByType: { type: string; count: number; percentage: number }[];
    uncategorizedServices: number;
    uncategorizedChange: number;
  };

  // B. Precios y Duración
  pricing: {
    averagePrice: number;
    priceRange: { min: number; max: number };
    averagePriceByType: { type: string; averagePrice: number }[];
    averageDuration: number;
    shortestServices: { service: string; duration: number }[];
    longestServices: { service: string; duration: number }[];
  };

  // C. Estadísticas de Ubicación
  location: {
    totalOffices: number;
    usageRate: number;
    officeUsage: { office: string; usageRate: number; appointments: number }[];
    usageChange: number;
  };

  // D. Estadísticas de Conexión
  performance: {
    topSellingServices: { service: string; sales: number; revenue: number }[];
    highestRevenueTypes: { type: string; revenue: number; growth: number }[];
    leastProfitableServices: {
      service: string;
      revenue: number;
      cost: number;
      profit: number;
    }[];
    busiestOffice: {
      office: string;
      appointments: number;
      utilization: number;
    };
  };
}

// Color palette for charts
const COLORS = [
  "#0D6EFD",
  "#264A86",
  "#198754",
  "#FFC107",
  "#DC3545",
  "#9AA7C3",
  "#415168",
  "#E6EEF9",
  "#074ab0",
  "#0959d1",
];

const ServicesStats: React.FC<TabComponentProps> = ({
  data,
  loading,
  error,
  dateRange,
  onRetry,
}) => {
  const [activeSection, setActiveSection] = useState<
    "inventory" | "pricing" | "location" | "performance"
  >("inventory");

  if (loading) {
    return (
      <div className="loading-state">
        <p>Loading services statistics...</p>
      </div>
    );
  }

  if (error) {
    return <Error403Page />;
  }

  if (!data) {
    return (
      <div className="empty-state">
        <p>No data available</p>
      </div>
    );
  }

  // Prepare data for charts
  const serviceDistributionData = data.inventory.servicesByType.map((item) => ({
    name: item.type,
    value: item.percentage,
    count: item.count,
  }));

  const priceByTypeData = data.pricing.averagePriceByType.map((item) => ({
    name: item.type,
    price: item.averagePrice,
  }));

  const officeUsageData = data.location.officeUsage.map((office) => ({
    name: office.office,
    usage: office.usageRate,
    appointments: office.appointments,
  }));

  const topSellingData = data.performance.topSellingServices.map((service) => ({
    name: service.service,
    sales: service.sales,
    revenue: service.revenue,
  }));

  const revenueByTypeData = data.performance.highestRevenueTypes.map(
    (type) => ({
      name: type.type,
      revenue: type.revenue,
      growth: type.growth,
    }),
  );

  const durationComparisonData = [
    ...data.pricing.longestServices.map((s) => ({
      name: s.service,
      duration: s.duration,
      type: "long",
    })),
    ...data.pricing.shortestServices.map((s) => ({
      name: s.service,
      duration: s.duration,
      type: "short",
    })),
  ];

  return (
    <div className="container">
      {/* Navigation Tabs */}
      <div className="stats-tabs">
        <div
          className={`stats-tab ${
            activeSection === "inventory" ? "active" : ""
          }`}
          onClick={() => setActiveSection("inventory")}
        >
          A. Inventario
        </div>
        <div
          className={`stats-tab ${activeSection === "pricing" ? "active" : ""}`}
          onClick={() => setActiveSection("pricing")}
        >
          B. Precios & Duración
        </div>
        <div
          className={`stats-tab ${
            activeSection === "location" ? "active" : ""
          }`}
          onClick={() => setActiveSection("location")}
        >
          C. Ubicación
        </div>
        <div
          className={`stats-tab ${
            activeSection === "performance" ? "active" : ""
          }`}
          onClick={() => setActiveSection("performance")}
        >
          D. Rendimiento
        </div>
      </div>

      {/* Section A: Inventario de Servicios */}
      {activeSection === "inventory" && (
        <div>
          <h2>Inventario de Servicios</h2>

          {/* KPI Grid */}
          <div className="stats-kpi-grid">
            <div className="stats-kpi-card">
              <div className="stats-kpi-card__title">
                Total Servicios Activos
              </div>
              <div className="stats-kpi-card__value">
                {data.inventory.totalActiveServices}
              </div>
              <div className="stats-kpi-card__subtext">
                Servicios en operación
              </div>
            </div>

            {/* <div className="stats-kpi-card">
              <div className="stats-kpi-card__title">Tipos de Servicio</div>
              <div className="stats-kpi-card__value">
                {data.inventory.totalServiceTypes}
              </div>
              <div className="stats-kpi-card__subtext">
                Categorías distintas
              </div>
            </div> */}

            {/* <div className="stats-kpi-card">
              <div className="stats-kpi-card__title">
                Servicios sin Categoría
              </div>
              <div className="stats-kpi-card__value">
                {data.inventory.uncategorizedServices}
              </div>
              <div className="stats-kpi-card__subtext">
                Sin tipo de servicio
              </div>
            </div> */}
          </div>

          {/* Charts Grid */}
          <div className="stats-details-grid">
            <div className="stats-chart-panel">
              <h3>Distribución por Tipo de Servicio</h3>
              <div style={{ height: "300px", marginTop: "var(--space-md)" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={serviceDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      // label={({ name, percent }) =>
                      //   `${name}: ${(percent * 100).toFixed(0)}%`
                      // }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {serviceDistributionData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value}%`, "Porcentaje"]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* <div className="stats-list-panel">
              <h3>Detalles por Tipo</h3>
              {data.inventory.servicesByType.map((item, index) => (
                <div key={index} className="stats-list-item">
                  <span className="stats-list-item-label">{item.type}</span>
                  <span className="stats-list-item-value">
                    {item.count} ({item.percentage}%)
                  </span>
                </div>
              ))}
            </div> */}
          </div>
        </div>
      )}

      {/* Section B: Precios y Duración */}
      {activeSection === "pricing" && (
        <div>
          <h2>Estadísticas de Precios y Duración</h2>

          <div className="stats-kpi-grid">
            <div className="stats-kpi-card">
              <div className="stats-kpi-card__title">Precio Promedio</div>
              <div className="stats-kpi-card__value">
                ${data.pricing.averagePrice}
              </div>
              <div className="stats-kpi-card__subtext">Precio general</div>
            </div>

            <div className="stats-kpi-card">
              <div className="stats-kpi-card__title">Rango de Precios</div>
              <div className="stats-kpi-card__value">
                ${data.pricing.priceRange.min} - ${data.pricing.priceRange.max}
              </div>
              <div className="stats-kpi-card__subtext">Min - Max</div>
            </div>

            <div className="stats-kpi-card">
              <div className="stats-kpi-card__title">Duración Promedio</div>
              <div className="stats-kpi-card__value">
                {Math.floor(data.pricing.averageDuration / 60)}h{" "}
                {data.pricing.averageDuration % 60}m
              </div>
              <div className="stats-kpi-card__subtext">Horas por servicio</div>
            </div>
          </div>

          <div className="stats-details-grid">
            <div className="stats-chart-panel">
              {/* <h3>Precio Promedio por Tipo</h3>
              <div style={{ height: "300px", marginTop: "var(--space-md)" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={priceByTypeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${value}`, "Precio"]} />
                    <Legend />
                    <Bar
                      dataKey="price"
                      name="Precio Promedio"
                      fill={COLORS[0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div> */}

              <div style={{ marginTop: "var(--space-lg)" }}>
                <h4>Comparación de Duración</h4>
                <div style={{ height: "200px", marginTop: "var(--space-md)" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={durationComparisonData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => [`${value}h`, "Duración"]}
                      />
                      <Legend />
                      <Bar
                        dataKey="duration"
                        name="Duración (horas)"
                        fill={COLORS[1]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="stats-list-panel">
              <h3>Precios por Tipo</h3>
              {data.pricing.averagePriceByType.map((item, index) => (
                <div key={index} className="stats-list-item">
                  <span className="stats-list-item-label">{item.type}</span>
                  <span className="stats-list-item-value">
                    ${item.averagePrice}
                  </span>
                </div>
              ))}

              <div style={{ marginTop: "var(--space-lg)" }}>
                <h4>Servicios Más Cortos</h4>
                {data.pricing.shortestServices.map((service, index) => (
                  <div key={index} className="stats-list-item">
                    <span className="stats-list-item-label">
                      {service.service}
                    </span>
                    <span className="stats-list-item-value">
                      {Math.floor(service.duration / 60)}h{" "}
                      {service.duration % 60}m
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section C: Estadísticas de Ubicación */}
      {activeSection === "location" && (
        <div>
          <h2>Estadísticas de Ubicación</h2>

          <div className="stats-kpi-grid">
            <div className="stats-kpi-card">
              <div className="stats-kpi-card__title">Total Oficinas</div>
              <div className="stats-kpi-card__value">
                {data.location.totalOffices}
              </div>
              <div className="stats-kpi-card__subtext">Ubicaciones activas</div>
            </div>
          </div>

          <div className="stats-details-grid">
            <div className="stats-chart-panel">
              <h3>Uso por Oficina</h3>
              <div style={{ height: "300px", marginTop: "var(--space-md)" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={officeUsageData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value}`, "Citas"]} />
                    <Legend />
                    {/* Only keep the appointments bar */}
                    <Bar dataKey="appointments" name="Citas" fill={COLORS[3]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="stats-list-panel">
              <h3>Detalles de Oficinas</h3>
              {data.location.officeUsage.map((office, index) => (
                <div key={index} className="stats-list-item">
                  <span className="stats-list-item-label">{office.office}</span>
                  <span className="stats-list-item-value">
                    {office.usageRate}% ({office.appointments} citas)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Section D: Estadísticas de Conexión */}
      {activeSection === "performance" && (
        <div>
          <h2>Estadísticas de Rendimiento</h2>

          <div className="stats-kpi-grid">
            <div className="stats-kpi-card">
              <div className="stats-kpi-card__title">Oficina Más Ocupada</div>
              <div
                className="stats-kpi-card__value"
                style={{ fontSize: "1.8rem" }}
              >
                {data.performance.busiestOffice.office}
              </div>
              <div className="stats-kpi-card__subtext">
                {data.performance.busiestOffice.appointments ?? "0"} citas
                <br />
                {data.performance.busiestOffice.utilization ?? "0"}% utilización
              </div>
            </div>
          </div>

          <div className="stats-details-grid">
            <div className="stats-chart-panel">
              <h3>Servicios Más Vendidos</h3>
              <div style={{ height: "300px", marginTop: "var(--space-md)" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topSellingData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === "sales") return [value, "Ventas"];
                        if (name === "revenue")
                          return [`$${value}`, "Ingresos"];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="sales"
                      name="Ventas"
                      fill={COLORS[0]}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="revenue"
                      name="Ingresos ($)"
                      fill={COLORS[1]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* 
              <div style={{ marginTop: "var(--space-lg)" }}>
                <h4>Tipos con Mayor Ingreso</h4>
                <div style={{ height: "200px", marginTop: "var(--space-md)" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueByTypeData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === "revenue")
                            return [`$${value}`, "Ingresos"];
                          if (name === "growth")
                            return [`${value}%`, "Crecimiento"];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="revenue"
                        name="Ingresos ($)"
                        fill={COLORS[4]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div> */}
            </div>

            <div className="stats-list-panel">
              <h3>Servicios Más Vendidos</h3>
              {data.performance.topSellingServices.map((service, index) => (
                <div key={index} className="stats-list-item">
                  <span className="stats-list-item-label">
                    {service.service}
                  </span>
                  <span className="stats-list-item-value">
                    {service.sales} ventas
                    <br />
                    <small>${service.revenue}</small>
                  </span>
                </div>
              ))}

              <div style={{ marginTop: "var(--space-lg)" }}>
                <h4>Servicios Menos Rentables</h4>
                {data.performance.leastProfitableServices.map(
                  (service, index) => (
                    <div key={index} className="stats-list-item">
                      <span className="stats-list-item-label">
                        {service.service}
                      </span>
                      <span
                        className="stats-list-item-value"
                        style={{
                          color:
                            service.profit >= 0
                              ? "var(--success)"
                              : "var(--error)",
                        }}
                      >
                        ${service.profit}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Range Display */}
      {dateRange && (
        <div
          style={{
            textAlign: "center",
            marginTop: "var(--space-md)",
            color: "var(--neutral-300)",
            fontSize: "0.9rem",
          }}
        >
          Mostrando datos del{" "}
          {new Date(dateRange.startDate).toLocaleDateString()} al{" "}
          {new Date(dateRange.endDate).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default ServicesStats;
